import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Mousewheel, Keyboard } from "swiper/modules";
import { useNavigate, useLocation } from "react-router-dom";
import ReelCard from "./ReelCard";
import api from "../api/axios"; // ✅ axios instance

const Reels = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const swiperRef = useRef(null);

  const [reels, setReels] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);

  const [cursor, setCursor] = useState(null);
const [hasMore, setHasMore] = useState(true);
const [loadingMore, setLoadingMore] = useState(false);

  // 🔥 Load reels via API
 const loadReels = async (isInitial = false) => {
  try {
    if (loadingMore) return;

    setLoadingMore(true);

    const res = await api.get("/reels", {
      params: {
        cursor: isInitial ? null : cursor,
        limit: 5,
      },
    });

    const newReels = res.data.reels || [];

    setReels((prev) =>
      isInitial ? newReels : [...prev, ...newReels]
    );

    setCursor(res.data.nextCursor);
    setHasMore(res.data.hasMore);

  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
    setLoadingMore(false);
  }
};

useEffect(() => {
  loadReels(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Restore reel position on back navigation
  useEffect(() => {
    const reelIndex = location.state?.reelIndex;
    if (
      typeof reelIndex === "number" &&
      swiperRef.current?.slideTo
    ) {
      swiperRef.current.slideTo(reelIndex, 0);
    }
  }, [location.state]);

  // ponytail: Lock body scroll to prevent Header + Layout minHeight overflow
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const activeReel = reels[activeIndex];

  if (loading) {
    return (
      <Box sx={{ height: { xs: 'calc(100dvh - 112px)', md: 'calc(100dvh - 64px)' }, display: "flex", bgcolor: "#000", overflow: "hidden" }}>
        {/* LEFT: REELS SKELETON */}
        <Box
          sx={{ flex: 1, display: "flex", justifyContent: "center", overflow: "hidden" }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 480,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Skeleton reel card */}
            <Box
              sx={{
                width: "100%",
                height: "80%",
                maxHeight: 600,
                backgroundColor: "#222",
                borderRadius: 2,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Skeleton video area */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#333",
                }}
              />
              
              {/* Skeleton content overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                }}
              >
                {/* Skeleton vendor info */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: "#444",
                    }}
                  />
                  <Box
                    sx={{
                      width: 100,
                      height: 16,
                      backgroundColor: "#444",
                      borderRadius: 1,
                    }}
                  />
                </Box>
                
                {/* Skeleton title */}
                <Box
                  sx={{
                    width: "80%",
                    height: 20,
                    backgroundColor: "#444",
                    borderRadius: 1,
                    mb: 1,
                  }}
                />
                
                {/* Skeleton rating */}
                <Box
                  sx={{
                    width: 60,
                    height: 16,
                    backgroundColor: "#444",
                    borderRadius: 1,
                    mb: 1,
                  }}
                />
                
                {/* Skeleton price and button */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 20,
                      backgroundColor: "#444",
                      borderRadius: 1,
                    }}
                  />
                  <Box
                    sx={{
                      width: 100,
                      height: 36,
                      backgroundColor: "#444",
                      borderRadius: "100px",
                    }}
                  />
                </Box>
              </Box>
              
              {/* Shimmer animation */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)",
                  animation: "shimmer 1.5s infinite",
                  "@keyframes shimmer": {
                    "0%": { transform: "translateX(-100%)" },
                    "100%": { transform: "translateX(100%)" },
                  },
                }}
              />
            </Box>
            
            {/* Loading text */}
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Loading reels...
            </Typography>
          </Box>
        </Box>
        
        {/* RIGHT PANEL SKELETON (DESKTOP) */}
        <Box
          sx={{
            width: 360,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            bgcolor: "#111",
            p: 2,
            gap: 2,
          }}
        >
          <Box sx={{ width: "100%", height: 20, backgroundColor: "#333", borderRadius: 1 }} />
          <Box sx={{ width: "80%", height: 16, backgroundColor: "#333", borderRadius: 1 }} />
          <Box sx={{ width: "70%", height: 16, backgroundColor: "#333", borderRadius: 1 }} />
          <Box sx={{ width: "90%", height: 16, backgroundColor: "#333", borderRadius: 1 }} />
          
          <Box sx={{ width: "100%", height: 1, backgroundColor: "#333", my: 1 }} />
          
          <Box sx={{ width: "100%", height: 20, backgroundColor: "#333", borderRadius: 1 }} />
          <Box sx={{ width: "100%", height: 40, backgroundColor: "#333", borderRadius: 1 }} />
          <Box sx={{ width: "100%", height: 40, backgroundColor: "#333", borderRadius: 1 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: { xs: 'calc(100dvh - 112px)', md: 'calc(100dvh - 64px)' }, display: "flex", bgcolor: "#000", overflow: "hidden" }}>
      {/* LEFT: REELS */}
      <Box
        className="reels-scroll-wrapper"
        sx={{ flex: 1, display: "flex", justifyContent: "center", overflow: "hidden" }}
      >
        <Swiper
          direction="vertical"
          modules={[Mousewheel, Keyboard]}
          mousewheel={{
            forceToAxis: true,
            eventsTarget: ".reels-scroll-wrapper"
          }}
          keyboard={{ enabled: true }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => {
              const index = swiper.activeIndex;
              setActiveIndex(index);

              // 🔥 PREFETCH when user reaches near end
              if (index >= reels.length - 2 && hasMore) {
                loadReels(false);
              }
            }}
          style={{ width: "100%", maxWidth: 480, height: "100%" }}
        >
          {reels.map((reel, index) => (
            <SwiperSlide key={reel.id}>
              <ReelCard
                reel={reel}
                index={index}
                isActive={index === activeIndex}
                audioEnabled={audioEnabled}
                onToggleAudio={() => setAudioEnabled((prev) => !prev)}
                onShopNow={() =>
                  navigate(`/delivery-location/${reel.id}`, {
                    state: { reelIndex: index },
                  })
                }
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {/* RIGHT PANEL (DESKTOP) */}
      <Box
        sx={{
          width: 360,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          bgcolor: "#111",
          color: "white",
          p: 2,
          gap: 2,
        }}
      >
        {activeReel && (
          <>
            <Stack spacing={1}>
              <Typography fontWeight="bold">
                Why buy from us?
              </Typography>
              <Typography variant="caption">🚚 Free Delivery</Typography>
              <Typography variant="caption">💳 Cash on Delivery</Typography>
              <Typography variant="caption">⭐ 4.5+ Rated Products</Typography>
            </Stack>

            <Divider sx={{ bgcolor: "#333" }} />

            <Stack spacing={1}>
              <Typography fontWeight="bold">Quick Actions</Typography>
              <Button startIcon={<FavoriteBorderIcon />}>
                Wishlist
              </Button>
              <Button startIcon={<LocalOfferIcon />}>
                View Offers
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Reels;